import {
  ALL_TIME_DATE_WINDOW,
  createLastNWindow,
  DataPointsApiResponse,
  DataRow,
  DataTable,
  DateTrunc,
  definitionWithWindow,
  getMeasureFormatString,
  materializeTable,
  maxPrecision,
  parseDataPoints,
} from '@tableau/metrics-engine';
import { MetricCardProps, MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom';

import { FeatureFlags } from '../ts/FeatureFlags';
import { DateGranularity, IMetricDataList } from '../ts/ServerApi/types/IMetricDataList';
import { IMetricInfo } from './models/IMetricInfo';

export const metricCardProps = (metric: DataPointsApiResponse): MetricCardProps => {
  const data = metric.data ? metric.data : [];
  const measureFormat = getMeasureFormatString(metric.definition);
  const definition = definitionWithWindow(metric.definition, createLastNWindow(100));

  const materializedData = materializeTable(definition, data);
  const dataToRender = materializedData.unwrapOr([]);

  const metricsReferenceLineEnabled = FeatureFlags.metricsReferenceLineEnabled;

  return {
    testId: 'metric-card',
    data: dataToRender,
    definition,
    lastUpdatedTimestamp: metric.dataRefreshedTimestamp,
    measureFormat,
    suspended: false,
    metricColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
    metricsReferenceLineEnabled,
  };
};

export const getTableWithDefaultComparison = (metric: DataPointsApiResponse): DataTable => {
  const data = metric.data ? metric.data : [];
  const definition = definitionWithWindow(metric.definition, createLastNWindow(100));
  const materializedData = materializeTable(definition, data);
  return materializedData.unwrapOr([]);
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];
  if (data) {
    return {
      ...metricInfo,
      dataPoints: parseDataPoints(data.points, maxPrecision(data.schema?.measure?.format)),
      schema: data.schema,
      dataRefreshedTimestamp: data.dataRefreshedTimestamp
    };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: DataPointsApiResponse | undefined, measureAccessor: (point: DataRow) => number): number | undefined => {
  return metric?.data?.length > 0 && measureAccessor(metric.data[metric.data.length - 1]);
};

export const metricDetailProps = (name: string, metric?: DataPointsApiResponse): MetricDetailViewProps => {
  const data = metric?.data ? metric.data : [];
  const definition = metric?.definition && definitionWithWindow(metric?.definition, ALL_TIME_DATE_WINDOW);
  const measureFormat = definition && getMeasureFormatString(definition);

  const dataToRender = materializeTable(definition, data).unwrapOr([]);

  return {
    metricName: name,
    testId: 'metric-detail',
    data: dataToRender,
    definition,
    lastUpdatedTimestamp: metric?.dataRefreshedTimestamp,
    measureFormat,
    metricsReferenceLineEnabled: FeatureFlags.metricsReferenceLineEnabled,
  };
};

export const dateTruncFromGranularity = (date?: DateGranularity): DateTrunc => {
  switch (date) {
    case DateGranularity.SECOND: {
      return DateTrunc.SECOND;
    }
    case DateGranularity.MINUTE: {
      return DateTrunc.MINUTE;
    }
    case DateGranularity.HOUR: {
      return DateTrunc.HOUR;
    }
    case DateGranularity.DAY: {
      return DateTrunc.DAY;
    }
    case DateGranularity.ISO_WEEKDAY: {
      return DateTrunc.ISO_WEEKDAY;
    }
    case DateGranularity.WEEK: {
      return DateTrunc.WEEK;
    }
    case DateGranularity.ISO_WEEK: {
      return DateTrunc.ISO_WEEK;
    }
    case DateGranularity.MONTH: {
      return DateTrunc.MONTH;
    }
    case DateGranularity.QUARTER: {
      return DateTrunc.QUARTER;
    }
    case DateGranularity.ISO_QUARTER: {
      return DateTrunc.ISO_QTR;
    }
    case DateGranularity.YEAR: {
      return DateTrunc.YEAR;
    }
    case DateGranularity.ISO_YEAR: {
      return DateTrunc.ISO_YEAR;
    }
    case DateGranularity.UNKNOWN:
    case undefined:
    default: {
      return DateTrunc.UNKNOWN;
    }
  }
};
